import Tabs from '../components/tabs/tabs';
import SpecialtyFilterTable from '../components/specialty-filter-table';
import ComplianceScore from '../components/compliance-score';
import NetworkMap from '../components/network-map';
import './networkAnalysisPage.scss';
import AdditionalProvidersTable from '../components/additional-providers-table';
import {Button, Layer, Modal, TextInput, TextArea, InlineNotification, SkeletonPlaceholder} from '@carbon/react';
import ComplianceSummaryTable from '../components/summary';
import {observer} from 'mobx-react';
import {useContext, useEffect, useState, useCallback, useMemo} from 'react';
import {useParams, Navigate} from 'react-router-dom';
import {NetworkContext} from '../lib/context';
import {ResetAlt} from '@carbon/icons-react';
import Spinner from '../../../components/spinner';
import NetworkAnalysisGeographyFilter from '../components/no-filters/network-analysis-geography-filter';
import DisruptionAnalysisAdditionalProviderTable from '../components/disruption-analysis-additional-provider-table';
import DisruptionAnalysisNetworkMap from '../components/disruption-analysis-network-map';
import MultiWayToggle from '../components/multi-way-toggle';
import DisruptionAnalysisSpecialtySummaryTable from '../components/specialty_summary_disruption_analysis';
import RevisionIdSelector from '../components/revision-id-selector';
import LoadingModal from '../components/loading-modal';
import DisruptionAnalysisFilter from '../components/no-filters/disruption-analysis-filter';
import {useSpecialtyDataSource} from '../components/hooks';
import Export from '../pages/reports-page/components/Export';
import ToastNotifications from '../components/toast-notifications';
import handleError from '../../../lib/error';
import checkAccess, {SiteFeature} from '../../../lib/check-access';
import {
    NetworkAnalysisTabId,
    NETWORK_ANALYSIS_TAB_CONFIGS,
    normalizeAndApply
} from './network-analysis-tab-config';

const PAGE_HEIGHT = 'min-h-[calc(100vh-11rem)]';
const DOWNLOAD_COMPLIANCE_SUMMARY_ITEM = 'complianceSummary';

// Extract ExploreView as a separate component to prevent re-creation
const ExploreView = observer(({ 
    handleSetSaveRosterModalOpen,
    specialtyLoading,
    specialtyError,
    mapDataLoading,
    mapDataError
}: { 
    handleSetSaveRosterModalOpen: (open: boolean) => void,
    specialtyLoading?: boolean,
    specialtyError?: string | null,
    mapDataLoading?: boolean,
    mapDataError?: string | null
}) => {
    return <div className="bg-background-gray-100 relative">
        <div
            className={`network-analysis-page grid grid-cols-9 grid-rows-9 gap-3 ${PAGE_HEIGHT} p-2 pl-6 mr-[calc(16rem+2rem)]`}>
            <div className="col-span-2 row-span-4">
                <ComplianceScore 
                    compactView
                    specialtyLoading={specialtyLoading}
                    specialtyError={specialtyError}
                />
            </div>
            <div className="col-span-7 row-span-4">
                <NetworkMap setSaveRosterModalOpen={handleSetSaveRosterModalOpen} mapDataLoading={mapDataLoading} mapDataError={mapDataError} />
            </div>
            <div className="col-span-9 row-span-5">
                <Layer className="h-full">
                    <AdditionalProvidersTable setSaveRosterModalOpen={handleSetSaveRosterModalOpen} />
                </Layer>
            </div>
        </div>
        <div className="network-analysis-page absolute right-0 top-0 min-h-[calc(100%)]">
            <NetworkAnalysisGeographyFilter activeTab={NetworkAnalysisTabId.Explore} />
        </div>
    </div>;
});

const SummaryView = observer(({
    specialtyLoading,
    specialtyError,
    exportButton
}: {
    specialtyLoading?: boolean,
    specialtyError?: string | null,
    exportButton?: React.ReactNode
}) => {
    return <div className="bg-background-gray-100 relative">
        <div className={`network-analysis-page grid grid-cols-1 grid-rows-1 gap-3 ${PAGE_HEIGHT} p-2 pl-6 mr-[calc(16rem+2rem)]`}>
            <div className="col-span-1 h-full">
                <ComplianceSummaryTable
                    specialtyLoading={specialtyLoading}
                    specialtyError={specialtyError}
                    exportButton={exportButton}
                />
            </div>
        </div>
        <div className='network-analysis-page absolute right-0 top-0 h-[calc(100%)]'>
            <NetworkAnalysisGeographyFilter activeTab={NetworkAnalysisTabId.Summary} />
        </div>
    </div>;
});

const ResultsView = observer(({
    handleSetSaveRosterModalOpen,
    specialtyLoading,
    specialtyError,
    mapDataLoading,
    mapDataError
}: {
    handleSetSaveRosterModalOpen: (open: boolean) => void,
    specialtyLoading?: boolean,
    specialtyError?: string | null,
    mapDataLoading?: boolean,
    mapDataError?: string | null
}) => {
    return <div className='bg-background-gray-100 relative'>
        <div className={`network-analysis-page grid grid-cols-[320px_1fr] grid-rows-5 gap-3 ${PAGE_HEIGHT} p-2 pl-6 mr-[calc(16rem+2rem)]`}>
            <div className="row-span-5">
                <SpecialtyFilterTable
                    fullHeight
                    specialtyLoading={specialtyLoading}
                    specialtyError={specialtyError}
                />
            </div>
            <div className="row-span-5">
                <NetworkMap
                    setSaveRosterModalOpen={handleSetSaveRosterModalOpen}
                    mapDataLoading={mapDataLoading}
                    mapDataError={mapDataError}
                />
            </div>
        </div>
        <div className='network-analysis-page absolute right-0 top-0 h-[calc(100%)]'>
            <NetworkAnalysisGeographyFilter activeTab={NetworkAnalysisTabId.Results} />
        </div>
    </div>;
});

const DisruptionAnalysis = observer(({ 
    handleSetSaveRosterModalOpen 
}: { 
    handleSetSaveRosterModalOpen: (open: boolean) => void 
}) => {
    const noStore = useContext(NetworkContext);
    const disruptionTabs = [
        {
            value: 'specialty-summary',
            label: 'Specialty Summary',
            content: <DisruptionAnalysis_SpecialtySummaryMapLayout />,
            ['data-testid']: 'tab-specialty-summary'
        },
        {
            value: 'network-map',
            label: 'Network Map',
            content: <DisruptionAnalysis_NetworkMapLayout handleSetSaveRosterModalOpen={handleSetSaveRosterModalOpen} />,
            ['data-testid']: 'tab-network-map'
        },
        {
            value: 'providers-table',
            label: 'Providers Table',
            content: <DisruptionAnalysis_ProviderTableLayout handleSetSaveRosterModalOpen={handleSetSaveRosterModalOpen} />,
            isDisabled: !noStore.disruptionAnalysisSpecialtyFilter,
            disabledTooltipText: 'Please select a specialty to view providers.',
            ['data-testid']: 'tab-providers-table'
        },
        {
            value: 'combined-view',
            label: 'Combined View',
            content: <DisruptionAnalysis_CombinedView handleSetSaveRosterModalOpen={handleSetSaveRosterModalOpen} />,
            ['data-testid']: 'tab-combined-view'
        }
    ];
    return (
        <div className="bg-background-gray-100 relative">
            <div className='network-analysis-page pt-2 pl-6 mr-[calc(16rem+2rem)]'>
                <MultiWayToggle
                    options={disruptionTabs}
                    alignment='right'
                />
            </div>
            <div className="network-analysis-page absolute right-0 top-0 h-[calc(100%)]">
                <DisruptionAnalysisFilter />
            </div>
        </div>
    );
});

const DisruptionAnalysis_SpecialtySummaryMapLayout = observer(() => {
    return <div className={`network-analysis-page grid grid-cols-9 grid-rows-9 gap-3 ${PAGE_HEIGHT} p-2 pr-0`}>
        <div className="col-span-9 row-span-8">
            <DisruptionAnalysisSpecialtySummaryTable/>
        </div>
    </div>;
});

const DisruptionAnalysis_NetworkMapLayout = observer(({ 
    handleSetSaveRosterModalOpen 
}: { 
    handleSetSaveRosterModalOpen: (open: boolean) => void 
}) => {
    return <div className={`network-analysis-page grid grid-cols-9 grid-rows-9 gap-3 ${PAGE_HEIGHT} p-2 pr-0`}>
        <div className="col-span-9 row-span-8">
            <DisruptionAnalysisNetworkMap setSaveRosterModalOpen={handleSetSaveRosterModalOpen} />
        </div>
    </div>;
});

const DisruptionAnalysis_ProviderTableLayout = observer(({ 
    handleSetSaveRosterModalOpen 
}: { 
    handleSetSaveRosterModalOpen: (open: boolean) => void 
}) => {
    return <div className={`network-analysis-page grid grid-cols-9 grid-rows-9 gap-3 ${PAGE_HEIGHT} p-2 pr-0`}>
        <div className="col-span-9 row-span-8">
            <Layer className="h-full">
                <DisruptionAnalysisAdditionalProviderTable setSaveRosterModalOpen={handleSetSaveRosterModalOpen} />
            </Layer>
        </div>
    </div>;
});

const DisruptionAnalysis_CombinedView = observer(({ 
    handleSetSaveRosterModalOpen 
}: { 
    handleSetSaveRosterModalOpen: (open: boolean) => void 
}) => {
    return <div className={`network-analysis-page grid grid-cols-9 grid-rows-9 gap-3 ${PAGE_HEIGHT} p-2 pr-0`}>
        <div className="col-span-9 row-span-4">
            <DisruptionAnalysisNetworkMap setSaveRosterModalOpen={handleSetSaveRosterModalOpen} />
        </div>
        <div className="col-span-9 row-span-4">
            <Layer className="h-full">
                <DisruptionAnalysisAdditionalProviderTable setSaveRosterModalOpen={handleSetSaveRosterModalOpen} />
            </Layer>
        </div>
    </div>;
});

function NetworkAnalysisPage() {
    const networkOptimizationStore = useContext(NetworkContext);
    const {rosterId} = useParams();
    const [loading, setLoading] = useState<boolean>(false);
    const [saveRosterModalOpen, setSaveRosterModalOpen] = useState(false);
    const [fileName, setFileName] = useState('');
    const [note, setNote] = useState('');
    const [successNotification, setSuccessNotification] = useState(false);
    const [specialtyLoadingCount, setSpecialtyLoadingCount] = useState<number>(0);
    const [specialtyError, setSpecialtyError] = useState<string | null>(null);
    const specialtyLoading = useMemo(() => specialtyLoadingCount > 0, [specialtyLoadingCount]);
    const [mapDataLoadingCount, setMapDataLoadingCount] = useState<number>(0);
    const mapDataLoading = useMemo(() => mapDataLoadingCount > 0, [mapDataLoadingCount]);
    const [mapDataError, setMapDataError] = useState<string | null>(null);

    const specialtyDataSource = useSpecialtyDataSource(
        networkOptimizationStore.defaultComplianceScores,
        networkOptimizationStore.complianceScores
    );

    useEffect(() => {
        networkOptimizationStore.resetGeographyFilter();
    }, []);

    // Reset filters when switching between tabs to prevent filter conflicts
    useEffect(() => {
        if (networkOptimizationStore.activeTab === NetworkAnalysisTabId.DisruptionAnalysis) {
            networkOptimizationStore.resetGeographyFilter();
        } else {
            networkOptimizationStore.resetDisruptionAnalysisFilter();

            const tab = networkOptimizationStore.activeTab as NetworkAnalysisTabId;

            // Normalize county: strip multi-value down to primary when switching to a single-select tab
            normalizeAndApply(
                networkOptimizationStore.appliedGeographyFilter.county,
                tab,
                'county',
                (v) => networkOptimizationStore.setGeographyFilter('county', v, false)
            );

            // Normalize specialty: strip multi-value down to primary when switching to a single-select tab
            normalizeAndApply(
                networkOptimizationStore.specialtyFilter,
                tab,
                'specialty',
                (v) => networkOptimizationStore.setSpecialtyFilter(v)
            );

        }
    }, [networkOptimizationStore.activeTab]);

    // Explore requires exactly one specialty that exists in the loaded dataset. Runs only once the
    // load has settled, and re-picks if the current selection is missing from the fresh data.
    useEffect(() => {
        if (
            networkOptimizationStore.activeTab !== NetworkAnalysisTabId.Explore ||
            networkOptimizationStore.loadingSpecialtyData ||
            specialtyDataSource.length === 0
        ) {
            return;
        }
        const currentCode = networkOptimizationStore.specialtyFilter;
        const currentIsValid = !!currentCode && specialtyDataSource.some((s: any) => s.specialtyCode === currentCode);
        if (currentIsValid) return;
        const firstCode = (specialtyDataSource[0] as any).specialtyCode;
        if (firstCode) {
            networkOptimizationStore.setSpecialtyFilter(firstCode);
        }
    }, [
        networkOptimizationStore.activeTab,
        networkOptimizationStore.loadingSpecialtyData,
        networkOptimizationStore.specialtyFilter,
        specialtyDataSource
    ]);

    // Update fileName when modal opens or roster name changes
    useEffect(() => {
        if (saveRosterModalOpen && networkOptimizationStore.rosterFileName) {
            setFileName(networkOptimizationStore.rosterFileName);
        }
    }, [saveRosterModalOpen, networkOptimizationStore.rosterFileName]);

    useEffect(() => {
        const controller = new AbortController();
        const loadData = async () => {
            setSpecialtyLoadingCount(count => count + 1);
            setSpecialtyError(null);
            try {
                if (networkOptimizationStore.activeTab === NetworkAnalysisTabId.DisruptionAnalysis) {
                    await networkOptimizationStore.loadDisruptionAnalysisSpecialtiesData(controller);
                } else {
                    await networkOptimizationStore.loadSpecialtiesData(controller);
                }
            } catch (err) {
                handleError(err as Error, setSpecialtyError);
                networkOptimizationStore.handleApiError(err, 'Failed to Load Specialty Data', () => loadData());
            } finally {
                setSpecialtyLoadingCount(count => count - 1);
            }
        };
        loadData();
        return () => controller.abort();
    }, [
        networkOptimizationStore.activeTab,
        networkOptimizationStore.appliedDisruptionAnalysisFilter.state,
        networkOptimizationStore.appliedDisruptionAnalysisFilter.standard_type,
        networkOptimizationStore.appliedDisruptionAnalysisFilter.patient_roster_name,
        networkOptimizationStore.runId,
        networkOptimizationStore.revisionId,
        networkOptimizationStore.readyToLoadData(),
        networkOptimizationStore.appliedGeographyFilter,
        networkOptimizationStore.distanceCalcType,
        networkOptimizationStore.rosterRunCount,
        networkOptimizationStore.loadingRevisionRecalculation
    ]);

    const shouldFetchMapData = useMemo(() => {
        return [NetworkAnalysisTabId.Results, NetworkAnalysisTabId.Explore].includes(networkOptimizationStore.activeTab);
    }, [networkOptimizationStore.activeTab]);

    useEffect(() => {
        if (shouldFetchMapData) {
            const controller = new AbortController();
            (async () => {
                try {
                    setMapDataLoadingCount(count => count + 1);
                    setMapDataError('');
                    await networkOptimizationStore.loadMapGeoJson(controller);
                } catch (error) {
                    handleError(error as Error, setMapDataError);
                } finally {
                    setMapDataLoadingCount(count => count - 1);
                }
            })();
            return () => controller.abort();
        }
    }, [
        shouldFetchMapData,
        networkOptimizationStore.appliedGeographyFilter,
        networkOptimizationStore.specialtyFilter,
        networkOptimizationStore.runId,
        networkOptimizationStore.revisionId,
        networkOptimizationStore.distanceCalcType,
        networkOptimizationStore.rosterRunCount
    ]);

    const handleSetSaveRosterModalOpen = useCallback((open: boolean) => {
        setSaveRosterModalOpen(open);
    }, []);

    const handleSaveNewRoster = useCallback(async () => {
        const controller = new AbortController();
        try {
            setSaveRosterModalOpen(false);
            const rosterIdNum = parseInt(rosterId || '0', 10);
            const result = await networkOptimizationStore.copyRoster(rosterIdNum, fileName, note, controller);

            if (result?.success && result.revisionId !== null) {
                // Switch to the newly created revision and update it with the changes
                networkOptimizationStore.setRevisionId(result.revisionId);
                await networkOptimizationStore.updateRoster(controller);
                setSuccessNotification(true);
            }

            setFileName('');
            setNote('');
        } catch (err) {
            networkOptimizationStore.handleApiError(err, 'Failed to Save Roster', () => handleSaveNewRoster());
        }
    }, [fileName, note, rosterId, networkOptimizationStore]);

    function revertToOriginal() {
        setLoading(true);
        networkOptimizationStore.revertProviderRosterToOriginal(new AbortController())
            .then(() => networkOptimizationStore.showRevertModal = false)
            .finally(() => setLoading(false));
    }

    const handleExportSummary = useCallback(() => {
        networkOptimizationStore.downloadComplianceSummary(DOWNLOAD_COMPLIANCE_SUMMARY_ITEM, new AbortController().signal);
    }, [networkOptimizationStore]);

    const summaryExportButton = useMemo(() => {
        // Enable export when:
        // 1. Roster is loaded (runId exists)
        // 2. Filters are properly set (readyToLoadData)
        // 3. There is data to export (complianceScores has data)
        // 4. Not currently loading specialty data
        const hasData = networkOptimizationStore.complianceScores.length > 0;
        const canExport = networkOptimizationStore.runId &&
                         networkOptimizationStore.readyToLoadData() &&
                         hasData &&
                         !specialtyLoading;
        const isExporting = networkOptimizationStore.downloadingItems[DOWNLOAD_COMPLIANCE_SUMMARY_ITEM];

        return (
            <Export
                isExporting={isExporting}
                menuItems={[
                    {
                        disabled: !canExport || isExporting,
                        label: 'Export as CSV',
                        onClick: handleExportSummary
                    }
                ]}
            />
        );
    }, [
        networkOptimizationStore.runId,
        networkOptimizationStore.revisionId,
        networkOptimizationStore.appliedGeographyFilter.state,
        networkOptimizationStore.appliedGeographyFilter.county,
        networkOptimizationStore.complianceScores.length,
        networkOptimizationStore.downloadingItems[DOWNLOAD_COMPLIANCE_SUMMARY_ITEM],
        specialtyLoading,
        handleExportSummary
    ]);

    if (!rosterId)
        return <Navigate to="/network-optimization/my-files" replace />;

    const tabs = useMemo(() => {
        const stateAndCountyRequired = networkOptimizationStore.appliedGeographyFilter.state === '' ||
            networkOptimizationStore.appliedGeographyFilter.county === '';

        return [
            {
                label: NETWORK_ANALYSIS_TAB_CONFIGS[NetworkAnalysisTabId.Summary].label,
                content: <SummaryView
                    specialtyLoading={specialtyLoading}
                    specialtyError={specialtyError}
                    exportButton={summaryExportButton}
                />,
            },
            {
                label: NETWORK_ANALYSIS_TAB_CONFIGS[NetworkAnalysisTabId.Results].label,
                content: <ResultsView
                    handleSetSaveRosterModalOpen={handleSetSaveRosterModalOpen}
                    specialtyLoading={specialtyLoading}
                    specialtyError={specialtyError}
                    mapDataLoading={mapDataLoading}
                    mapDataError={mapDataError}
                />,
                isDisabled: NETWORK_ANALYSIS_TAB_CONFIGS[NetworkAnalysisTabId.Results].requiresStateAndCounty && stateAndCountyRequired,
                disabledTooltipText: 'Please select a state and county'
            },
            {
                label: NETWORK_ANALYSIS_TAB_CONFIGS[NetworkAnalysisTabId.Explore].label,
                content: <ExploreView
                    handleSetSaveRosterModalOpen={handleSetSaveRosterModalOpen}
                    specialtyLoading={specialtyLoading}
                    specialtyError={specialtyError}
                    mapDataLoading={mapDataLoading}
                    mapDataError={mapDataError}
                />,
                isDisabled: NETWORK_ANALYSIS_TAB_CONFIGS[NetworkAnalysisTabId.Explore].requiresStateAndCounty && stateAndCountyRequired,
                disabledTooltipText: 'Please select a state and county'
            },
            {
                label: NETWORK_ANALYSIS_TAB_CONFIGS[NetworkAnalysisTabId.DisruptionAnalysis].label,
                content: <DisruptionAnalysis handleSetSaveRosterModalOpen={handleSetSaveRosterModalOpen} />,
                hideTab: !checkAccess(SiteFeature.NetworkOptimizationRosterComparison),
            },
        ];
    }, [
        handleSetSaveRosterModalOpen,
        specialtyLoading,
        specialtyError,
        mapDataLoading,
        mapDataError,
        networkOptimizationStore.appliedGeographyFilter.state,
        networkOptimizationStore.appliedGeographyFilter.county,
        summaryExportButton
    ]);
    
    return <div className="network-analysis-page pt-5 bg-white">
        <h1 className="m-0 ml-12 mb-1 text-[28px] font-normal font-inherit">Network Analysis</h1>
        <div className="relative">
            <div className="m-0 ml-12 mb-1 text-[14px]">
                {networkOptimizationStore.rosterFileName ?
                    <div className="italic mb-4">{networkOptimizationStore.rosterFileName}</div> :
                    <SkeletonPlaceholder className='w-48 h-5 mb-4' />
                }
                <RevisionIdSelector rosterId={rosterId}/>
                {successNotification && (
                    <InlineNotification
                        kind="success"
                        subtitle="Use the dropdown to switch between the original and revised version(s) of this roster anytime."
                        title="New roster created"
                        onCloseButtonClick={() => setSuccessNotification(false)}
                        hideCloseButton={false}
                        className='inline-notification'
                    />
                )}
            </div>
            {networkOptimizationStore.revisionId > 0 && <div className="revert-revision">
                <Button kind="tertiary" renderIcon={ResetAlt} onClick={() => networkOptimizationStore.showRevertModal = true}>Revert to Original</Button>
            </div>}
        </div>         
        <Tabs tabs={tabs.filter(tab => !tab.hideTab)} onClick={(i) => networkOptimizationStore.activeTab = i}/>
        <Modal open={networkOptimizationStore.showRevertModal} size="sm" modalHeading="Revert File to Original Version"
            secondaryButtonText="Cancel" primaryButtonText="Revert to Original"
            onRequestClose={() => networkOptimizationStore.showRevertModal = false}
            onSecondarySubmit={() => networkOptimizationStore.showRevertModal = false}
            onRequestSubmit={revertToOriginal}>
            <p>Unsaved changes have been made to this revised version of the current file/roster. Would you like to
                discard these changes and revert to the original file/roster?</p>
            {loading && <div className="w-full flex justify-end">
                <Spinner />
            </div>}
        </Modal>
        
        {/* Save New Roster Modal */}
        <Modal
            open={saveRosterModalOpen}
            onRequestClose={() => setSaveRosterModalOpen(false)}
            modalHeading="Save New Roster"
            primaryButtonText="Save"
            secondaryButtonText="Cancel"
            onRequestSubmit={handleSaveNewRoster}
            primaryButtonDisabled={!fileName.trim()}
            size="sm"
        >
            <div className="mb-4">
                <TextInput
                    id="fileName"
                    labelText="File Name"
                    value={fileName}
                    onChange={(e) => setFileName(e.target.value)}
                    placeholder="Enter file name"
                />
            </div>
            <div className="mb-4">
                <TextArea
                    id="note"
                    labelText="Note (Optional)"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Add a note for this roster revision"
                    rows={3}
                />
            </div>
        </Modal>
        
        {/* Loading Modal */}
        <LoadingModal open={networkOptimizationStore.isRosterActionLoading} />

        {/* Recalculation Modal */}
        <Modal open={networkOptimizationStore.loadingRevisionRecalculation} modalLabel="Processing your data" passiveModal preventCloseOnClickOutside>
            <div className='w-full p-4 flex space-x-8 items-center'>
                <Spinner />
                <div>
                    <p>Thank you for your patience! Recalculating your network adequacy may take a few minutes. <br />
                        <strong>Please avoid refreshing your browser.</strong></p>
                </div>
            </div>
        </Modal>

        <ToastNotifications />
    </div>;
}

export default observer(NetworkAnalysisPage);


